import { commonConstantTexts } from 'constants/constantText';
import moment from 'moment';
import { countryType } from './Constants';
import { type Fund } from 'store/fund';

/**
 * Converts an array into a matrix with specified number of elements per sub-array
 * @param list - The input array to be converted
 * @param elementsPerSubArray - Number of elements in each sub-array
 * @returns Matrix (2D array) with the specified structure
 */
export function arrayToMatrix<T>(
  list: T[],
  elementsPerSubArray: number,
): T[][] {
  const matrix: T[][] = [];
  let currentIndex: number;
  let matrixIndex: number;

  for (currentIndex = 0, matrixIndex = -1; currentIndex < list.length; currentIndex += 1) {
    if (currentIndex % elementsPerSubArray === 0) {
      matrixIndex += 1;
      matrix[matrixIndex] = [];
    }
    matrix[matrixIndex].push(list[currentIndex]);
  }

  return matrix;
}

/**
 * Detects the operating system based on navigator.appVersion
 * @returns String representing the detected operating system
 */
export function getOperatingSystem(): string {
  let OSName = 'Unknown OS';
  if (navigator.appVersion.includes('Win')) OSName = 'Windows';
  if (navigator.appVersion.includes('Mac')) OSName = 'MacOS';
  if (navigator.appVersion.includes('X11')) OSName = 'UNIX';
  if (navigator.appVersion.includes('Linux')) OSName = 'Linux';
  return OSName;
}

/**
 * Formats a date string to MM/DD/YYYY format with timezone adjustment
 * @param dateString - The date string to format
 * @returns Formatted date string or '-' if invalid
 */
export function dateFormater(dateString: string): string {
  let result = '-';
  if (dateString) {
    const dateObject = new Date(
      new Date(new Date(dateString)).getTime() -
      new Date(new Date()).getTimezoneOffset() * -60000,
    );
    result = `${dateObject.getMonth() + 1}/${dateObject.getDate()}/${dateObject.getFullYear()}`;
  }
  return result;
}

/**
 * Formats a date string to determine company year (current year or next year based on month)
 * @param dateString - The date string to format
 * @returns Company year as string or '-' if invalid
 */
export function compYearFormater(dateString: string): string {
  let result = '-';
  if (dateString !== '') {
    const dateObject = new Date(
      new Date(new Date(dateString)).getTime() -
      new Date(new Date()).getTimezoneOffset() * -60000,
    );
    result = `${dateObject.getMonth() < 10 ? dateObject.getFullYear() : dateObject.getFullYear() + 1}`;
  }
  return result;
}

/**
 * Formats a date string to MM/DD/YYYY format, subtracting 60 days from the input date
 * @param dateString - The date string to format
 * @returns Formatted date string or '-' if invalid
 */
export function endDateFormater(dateString: string): string {
  let result = '-';
  if (dateString !== '') {
    const dateObject = new Date(
      new Date(new Date(dateString)).getTime() -
      new Date(new Date()).getTimezoneOffset() * -60000 -
      60000 * 24 * 60, // Subtract 60 days
    );
    result = `${dateObject.getMonth() + 1}/${dateObject.getDate()}/${dateObject.getFullYear()}`;
  }
  return result;
}

/**
 * Adds one year to the given date and formats it to YYYY-MM-DD
 * @param dateString - The date string to add one year to
 * @returns Formatted date string with one year added or '-' if invalid
 */
export function addOneYearFormater(dateString: string): string {
  let result = '-';
  if (dateString) {
    const fullYear =
      new Date(
        new Date(new Date(dateString)).getTime() -
        new Date(new Date()).getTimezoneOffset() * -60000,
      ).getFullYear() + 1;

    const addDate = new Date(
      new Date(new Date(dateString)).getTime() -
      new Date(new Date()).getTimezoneOffset() * -60000,
    ).setFullYear(fullYear);
    result = moment(addDate).format('YYYY-MM-DD');
  }
  return result;
}

/**
 * Formats a number to 2 decimal places and removes trailing zeros
 * @param values - The number to format
 * @returns Formatted number as string
 */
export function tofixedNumber(values: number): string {
  return values.toFixed(2).replace(/(\.0{1,4})$/, '')
}

/**
 * Validates if a date string is valid using moment.js
 * @param dateString - The date string to validate
 * @returns True if the date is valid, false otherwise
 */
export function invalidDateChecker(dateString: string): boolean {
  return moment(
    moment(dateString).format('MM/DD/YYYY'),
    'MM/DD/YYYY',
    true,
  ).isValid();
}

/**
 * Formats a date string to DD-MM-YYYY format using the common date format constant
 * @param dateString - The date string or Date object to format
 * @returns Formatted date string or empty string if invalid
 */
export function dateFormat(dateString: string | Date): string {
  return dateString && dateString !== '-' ? moment(dateString).format(commonConstantTexts.dateFormat) : ''
}

export function safeDateFormat(dateString?: string | null, fallback: string = '-'): string {
  if (!dateString || dateString === '-') return fallback;

  const normalizedDate = dateString.trim();
  if (!normalizedDate || normalizedDate.toLowerCase() === 'invalid date') return fallback;

  const parsedDate = moment(
    normalizedDate,
    [
      moment.ISO_8601,
      'YYYY-MM-DD',
      'YYYY-MM-DD HH:mm:ss',
      'YYYY-MM-DDTHH:mm:ss.SSSZ',
      'YYYY-MM-DDTHH:mm:ssZ',
      'DD/MM/YYYY',
      'D/M/YYYY',
      'MM/DD/YYYY',
      'M/D/YYYY',
    ],
    true,
  );

  return parsedDate.isValid()
    ? parsedDate.format(commonConstantTexts.dateFormat)
    : fallback;
}

/**
 * Creates sorting parameters array from page info object
 * @param pageInfo - Object containing sortBy and sortOrder properties
 * @returns Array of sorting objects or empty array if no sorting info
 */
export function checkSortingValues(pageInfo: object): object[] {
  const sortValues = pageInfo?.sortBy && pageInfo?.sortOrder
    ? [
      {
        field: pageInfo?.sortBy,
        sort: pageInfo?.sortOrder,
      },
    ]
    : []
  return sortValues;
}

/**
 * Formats a number to locale string with 7 decimal places for unit display
 * @param num - The number to format
 * @returns Formatted number as string or number
 */
export function UnitFormater(num: number | string): string | number {
  return num?.toLocaleString(countryType.EURO, {
    minimumFractionDigits: 7,
    maximumFractionDigits: 7,
  });
}

/**
 * Converts a date string to ISO format with timezone offset
 * @param dateStr - The date string to convert
 * @returns ISO formatted date string with timezone
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const convertToISOFormat = (dateStr: string) => {
  const date = new Date(dateStr);
  // Adjust to the desired format: yyyy-MM-dd HH:mm:ss.SSS ±HH:mm
  const formattedDate = date.toISOString().replace('T', ' ').replace('Z', ' +00:00');
  return formattedDate;
};

/**
 * Formats a number to a string with specified decimal places, padding with zeros if needed
 * @param value - The value to format (number, string, undefined, or null)
 * @param decimalPlaces - Number of decimal places to include
 * @returns Formatted string with specified decimal places
 */
export function unitsAndAmountSplitStringFormater(
  value: number | string | undefined | null,
  decimalPlaces: number
): string {
  if (value === undefined || value === null || value === '' || isNaN(Number(value))) {
    return `0.${'0'.repeat(decimalPlaces)}`;
  }

  const numValue = Number(value);
  const [integerPart, decimalPart = ''] = numValue.toString().split('.');

  return `${integerPart}.${decimalPart.padEnd(decimalPlaces, '0').substring(0, decimalPlaces)}`;
}

/**
 * Finds fund description and short description from master funds array
 * @param allMasterFunds - Array of all master funds
 * @param fundValue - The fund name to search for
 * @returns Object containing shortDescription and description
 */
export const getDescriptionAndShortDescription = (allMasterFunds: Fund[], fundValue: string): {
  shortDescription: string,
  description: string
} => {
  const fund = allMasterFunds.find((val) => val.name === fundValue);

  return {
    shortDescription: fund?.shortDescription ?? '',
    description: fund?.description ?? ''
  };
};

/**
 * Formats a number to exactly 7 decimal places
 * @param value - The number or string to format
 * @returns Number formatted to 7 decimal places
 */
export const formatToSevenFixedDecimals = (value: number | string): string | number => {
  const sanitizedValue = typeof value === 'string' ? value.replace(/€/g, '').trim() : value;
  return Number(sanitizedValue).toFixed(7);
};

/**
 * Formats a number to exactly 2 decimal places
 * @param value - The number or string to format
 * @returns Number formatted to 2 decimal places
 */
export const formatToTwoFixedDecimals = (value: number | string): string | number => {
  const sanitizedValue = typeof value === 'string' ? value.replace(/€/g, '').trim() : value;
  return parseFloat(Number(sanitizedValue).toFixed(2));
};

/**
 * Truncates an email address to specified maximum length with ellipsis
 * @param email - The email address to truncate
 * @param maxLength - Maximum length before truncation (default: 25)
 * @returns Truncated email string
 */
export const truncateEmail = (email: string, maxLength: number = 25): string => {
  if (email.length <= maxLength) return email;
  return email.slice(0, maxLength - 3) + "...";
};

/**
 * Prevents specific keys from being entered in number input fields
 * @param e - Keyboard event object
 */
export const handleNumberInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
  if (['e', 'E', '+', '-'].includes(e.key)) {
    e.preventDefault();
  }
};

/**
 * Gets tab index from localStorage based on stored tab value
 * @param key - The localStorage key to check
 * @returns Tab index number (9 for complaints, 1 for withdrawalRequests, 0 for default)
 */
export const getTabIndexFromLocalStorage = (key: string): number => {
  const value = sessionStorage.getItem(key);
  // if local storage has tab value set current to complaints
  if (value && value === 'complaints') {
    return 9;
  } else if (value && value === 'withdrawalRequests') {
    return 1;
  } else {
    return 0;
  }
}

/**
 * Calculates the difference in days between a target date and current date
 * @param dateString - The target date string
 * @returns Number of days difference (positive for future dates, negative for past dates)
 */
export const getNoOfDayDiff = (dateString: string): number => {
  const now: Date = new Date();
  const targetDate: Date = new Date(dateString);

  const diffInMs: number = targetDate.getTime() - now.getTime();
  const diffInDays: number = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  return diffInDays;
}

/**
 * Formats a date string from YYYY-MM-DD to DD/MM/YYYY format
 * @param dateString - The date string in YYYY-MM-DD format
 * @returns Formatted date string in DD/MM/YYYY format or empty string if invalid
 */
export const formatDate = (dateString: string): string => {
  if (!dateString) return '';

  const [year, month, day] = dateString.split('-');
  return `${day}/${month}/${year}`;
};

type FieldType = 'amount' | 'units';
type TransactionType = 'Buy' | 'Sell' | 'Fees';

const signMap: Record<FieldType, Record<TransactionType, string>> = {
  amount: {
    Buy: '-',
    Sell: '+',
    Fees: '-',
  },
  units: {
    Buy: '+',
    Sell: '-',
    Fees: '-',
  },
};

export const getSign = (type: TransactionType, field: FieldType): string => {
  return signMap[field]?.[type] || '';
};

export const capitalizeWords = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

